// Per-project quotas admin page (v102, v211 copy refresh).
//
// Storage-only after v218 dropped the artifact-count axis — the
// count limit was unused in operations reviews and a project with
// 10k tiny .pom files is no more problematic than one with 100 big
// .tar.gz of the same total size.

import { useState } from 'react';
import { Save, Database, Infinity as InfinityIcon, AlertCircle } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useQuotas, setQuota, type QuotaRow } from '../../api/quotas';

function fmtBytes(n: number): string {
  if (n === 0) return '∞';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}

function pct(used: number, max: number): number {
  if (max === 0) return 0;
  return Math.min(100, (used / max) * 100);
}

function pctTone(p: number): string {
  if (p > 90) return 'bg-danger';
  if (p > 75) return 'bg-warning';
  return 'bg-accent';
}

function QuotaRowEditor({ row, onSave }: Readonly<{ row: QuotaRow; onSave: (gb: number) => void }>) {
  const [gb, setGb] = useState(row.storage_bytes_max ? row.storage_bytes_max / 1024 / 1024 / 1024 : 0);

  return (
    <tr className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
      <td className="px-5 py-3">
        <div>
          <p className="text-fg font-medium">{row.name}</p>
          <p className="text-xs text-fg-muted font-mono">{row.slug}</p>
        </div>
      </td>
      <td className="px-5 py-3 w-48">
        <div>
          <p className="text-xs text-fg-muted">{fmtBytes(row.storage_bytes_used)} / {fmtBytes(row.storage_bytes_max)}</p>
          <div className="h-1.5 mt-1 bg-bg-inset rounded">
            <div
              className={'h-1.5 rounded ' + pctTone(pct(row.storage_bytes_used, row.storage_bytes_max))}
              style={{ width: pct(row.storage_bytes_used, row.storage_bytes_max) + '%' }}
            />
          </div>
        </div>
      </td>
      <td className="px-5 py-3 w-36">
        <div className="relative">
          <Input
            type="number"
            value={gb}
            step={0.1}
            min={0}
            onChange={(e) => setGb(parseFloat(e.target.value) || 0)}
            placeholder="0 = unlimited"
          />
          {gb === 0 && (
            <InfinityIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-subtle pointer-events-none" />
          )}
        </div>
      </td>
      <td className="px-5 py-3 w-24">
        <Button variant="secondary" onClick={() => onSave(Math.round(gb * 1024 * 1024 * 1024))}>
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </td>
    </tr>
  );
}

export function AdminQuotas() {
  const { data, mutate } = useQuotas();

  const handleSave = async (projectId: string, storageBytesMax: number) => {
    try {
      await setQuota(projectId, { storage_bytes_max: storageBytesMax });
      await mutate();
    } catch (e) {
      alert(`Failed: ${e}`);
    }
  };

  return (
    <AdminShell
      title="Project quotas"
      description="Set per-project limits on how much storage a project can hold. The limit is evaluated on every upload — once a project reaches its cap, the next upload is rejected and the developer's CI tool surfaces a clear error message. Set the value to 0 to leave the project unlimited."
    >
      <Card>
        <CardBody className="flex items-start gap-3 text-sm">
          <AlertCircle className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          <div className="text-fg-muted leading-relaxed">
            <p className="text-fg font-medium mb-1">How quotas behave when reached</p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li>Existing artifacts stay accessible — pulls are never blocked by a quota.</li>
              <li>New uploads return HTTP <span className="font-mono text-xs">507 Insufficient Storage</span> with the body <span className="font-mono text-xs">"project storage quota exceeded"</span>.</li>
              <li>To free up room: delete unused artifacts in the project's repos, or raise the limit here. Retention policies (Lifecycle → Retention) can do the cleanup automatically.</li>
              <li>Usage bars turn amber at 75% and red at 90% so you can act before a CI pipeline breaks.</li>
            </ul>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Projects (${data?.length ?? 0})`}
          description="Click into a Storage limit cell to edit, then Save. Changes apply on the next upload — no restart needed."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Project</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Storage used</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Storage limit (GB)</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((row) => (
                  <QuotaRowEditor
                    key={row.project_id}
                    row={row}
                    onSave={(b) => handleSave(row.project_id, b)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-fg-muted text-sm">
                    <Database className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                    No projects yet — quotas activate as soon as you create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminShell>
  );
}
